import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useState } from 'react';

import {
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  Dialog,
  Divider,
  InfoTipIcon,
  Input,
  RevelioTooltip,
  ThemedSearchIcon,
} from '@revelio/ui';

import styles from '../../Modal/themedModal.module.css';
import footerStyles from './column-select-modal.module.css';

interface ColumnSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  columns: { value?: string; label?: string; tooltip?: string }[];
  setSavedCheckedColumns: (value: string[]) => void;
  savedCheckedColumns: string[];
  initialCheckedColumn?: string[];
  resetCheckedColumns?: string[];
  onSave?: (checkedColumns: string[]) => void;
}

export const ColumnSelectModal = ({
  isOpen,
  onClose,
  columns,
  setSavedCheckedColumns,
  savedCheckedColumns,
  initialCheckedColumn,
  resetCheckedColumns,
  onSave,
}: ColumnSelectModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [checkedColumns, setCheckedColumns] = useState<string[]>([]);
  const [searchedColumns, setSearchedColumns] = useState(columns);
  const [columnHeaders, setColumnHeaders] = useState<string[]>([]);

  useEffect(() => {
    const searchedCols = columns.filter((column) =>
      column?.value?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSearchedColumns(searchedCols);
  }, [columns, searchTerm]);

  const newColumnHeaders = useMemo(
    () => columns.map((col) => col?.value || ''),
    [columns]
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!isEqual(columnHeaders, newColumnHeaders)) {
      setColumnHeaders(newColumnHeaders);

      // Apply defaults when we have initialCheckedColumn
      if (initialCheckedColumn && newColumnHeaders.length > 0) {
        const defaultCheckedColumns = newColumnHeaders.filter((col) =>
          initialCheckedColumn?.includes(col)
        );
        if (defaultCheckedColumns?.length > 0) {
          setCheckedColumns(defaultCheckedColumns);
          setSavedCheckedColumns(defaultCheckedColumns);
        }
      }
    }
  }, [
    isOpen,
    newColumnHeaders,
    columnHeaders,
    setSavedCheckedColumns,
    initialCheckedColumn,
  ]);

  useEffect(() => {
    if (isOpen) {
      setCheckedColumns(savedCheckedColumns);
    }

    setSearchTerm('');
  }, [isOpen, savedCheckedColumns]);

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const onChangeColumns = (values: string[]) => {
    const sortedValues = columns
      .filter((col): col is { value: string; label?: string } => {
        return typeof col.value === 'string' && values.includes(col.value);
      })
      .map((col) => col.value);
    setCheckedColumns(sortedValues);
  };

  const onSaveColumn = () => {
    setSavedCheckedColumns(checkedColumns);
    if (onSave) {
      onSave(checkedColumns);
    }
    onClose();
  };

  const columnsToShow = searchedColumns.map((column) => ({
    value: column?.value,
    label: column?.label,
    tooltip: column?.tooltip,
  }));
  const resetColumns = useMemo(() => {
    const resetSource = resetCheckedColumns ?? initialCheckedColumn;
    if (!resetSource || resetSource.length === 0) {
      return [];
    }

    return columns
      .filter((col): col is { value: string; label?: string } => {
        return typeof col.value === 'string' && resetSource.includes(col.value);
      })
      .map((col) => col.value);
  }, [columns, initialCheckedColumn, resetCheckedColumns]);

  const onResetSelections = () => {
    setCheckedColumns(resetColumns);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Manage Columns"
      size="lg"
      className={styles.content}
      footer={
        <div className={footerStyles.footer}>
          <div className={footerStyles.footerActions}>
            {(resetCheckedColumns || initialCheckedColumn) && (
              <Button
                variant="ghost"
                size="sm"
                color="destructive"
                className={footerStyles.fontXs}
                onClick={onResetSelections}
                data-testid="reset-table-columns-button"
              >
                Reset
              </Button>
            )}
          </div>
          <ButtonGroup style={{ gap: 'var(--ui-spacing-4)' }}>
            <Button
              variant="link"
              size="sm"
              className={classNames(
                footerStyles.fontXs,
                footerStyles.linkAction
              )}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="default"
              size="sm"
              className={footerStyles.fontXs}
              onClick={onSaveColumn}
              data-testid="save-table-columns-button"
            >
              Save
            </Button>
          </ButtonGroup>
        </div>
      }
    >
      <Divider className={footerStyles.headerRule} orientation="horizontal" />
      <Input
        placeholder="Search"
        value={searchTerm}
        onChange={onChangeSearch}
        size="sm"
        wrapperClassName={styles.searchField}
        endSlot={<ThemedSearchIcon />}
      />
      <div className={classNames(styles.scrollArea, footerStyles.columnScroll)}>
        <CheckboxGroup value={checkedColumns} onChange={onChangeColumns}>
          <div className={footerStyles.columnList}>
            {columnsToShow
              .filter((column) => column.label)
              .map((column) => {
                return (
                  <Checkbox
                    key={column.value}
                    value={column.value}
                    data-testid={column.value + '-checkbox'}
                    className={footerStyles.checkbox}
                    labelSize="md"
                  >
                    <div className={footerStyles.columnLabel}>
                      {column.label}
                      {column.tooltip && (
                        <RevelioTooltip
                          label={column.tooltip}
                          placement="top"
                          openDelay={200}
                        >
                          <InfoTipIcon
                            size={12}
                            className={styles.muted}
                            tabIndex={0}
                            data-testid={`${column.value}-tooltip`}
                          />
                        </RevelioTooltip>
                      )}
                    </div>
                  </Checkbox>
                );
              })}
          </div>
        </CheckboxGroup>
      </div>
    </Dialog>
  );
};
